import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { portalPublishViewSchema } from "@shared/portal";
import { apiRequest, parseJsonResponse } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";

export function PublishControls({ orderId, busy, onRun, phaseMode = false }: {
  orderId: string; busy: boolean; onRun: (key: string, dryRun: boolean) => void;
  phaseMode?: boolean;
}) {
  const client = useQueryClient(), [confirmRevoke, setConfirmRevoke] = useState(false);
  const url = `/api/admin/smb/orders/${encodeURIComponent(orderId)}/publish`;
  const view = useQuery({
    queryKey: [url],
    queryFn: async () => portalPublishViewSchema.parse(await parseJsonResponse(await apiRequest("GET", url))),
  });
  const revoke = useMutation({
    mutationFn: () => apiRequest("POST", `${url}/revoke`, {}),
    onSuccess: async () => {
      setConfirmRevoke(false);
      await client.invalidateQueries({ queryKey: [url] });
      await client.invalidateQueries({ queryKey: ["/api/admin/smb/orders", orderId] });
    },
  });
  if (view.isPending) return <p className="text-sm">Loading publish inspection...</p>;
  if (!view.data || view.isError) return <div role="alert" className="text-sm">
    Publish inspection is unavailable. Make the package ready and check the existing file references.
    <Button variant="outline" size="sm" onClick={() => void view.refetch()}>Refresh inspection</Button>
  </div>;
  const data = view.data, delivery = data.delivery;
  const active = busy || revoke.isPending;
  return <div className="space-y-4" data-testid="publish-controls">
    <div role="note" className="rounded-lg border bg-muted/30 p-3 text-sm">
      <strong>Existing files</strong>
      <p>Publishing freezes the approved file references for the client delivery UI. It does not copy files or create ZIPs.</p>
      <p>{data.warning}</p>
    </div>
    {data.branding && <p className="text-sm font-medium">{data.branding.name}: {data.assets.filter(a => a.type !== "logo").length} approved assets</p>}
    {!data.canPublish && !delivery && <p className="text-sm">Only ready, fully reviewed orders can publish.</p>}
    {delivery && <div className="rounded-lg border p-3 text-sm">
      <p>Delivery: <strong>{delivery.status}</strong></p>
      {delivery.status === "preparing" && <p>This historical delivery is unfinished. File copying is no longer supported.</p>}
      {delivery.status === "revoked" && <p>Portal access is revoked. This snapshot cannot be republished.</p>}
    </div>}
    <div className="max-h-72 space-y-2 overflow-auto">
      {data.groups?.map(group => <section key={group.id}>
        <h4 className="text-sm font-semibold">{group.name}</h4>
        {data.assets.filter(asset => asset.personaId === group.id).map(asset => <div key={asset.id} className="flex flex-wrap items-center justify-between gap-3 border-b py-2 text-xs">
          {delivery?.status === "published" && (asset.type === "image"
            ? <img src={`${url}/assets/${encodeURIComponent(asset.id)}/preview`} alt={`Frozen preview: ${asset.name}`}
              referrerPolicy="no-referrer" loading="lazy" className="h-24 w-24 rounded border object-contain" />
            : asset.type === "video" ? <video src={`${url}/assets/${encodeURIComponent(asset.id)}/preview`}
              controls preload="none" className="max-h-32 w-40 rounded border" aria-label={`Frozen preview: ${asset.name}`} /> : null)}
          <span>{asset.name} ({asset.tool})</span>
          {asset.sizeBytes !== null && <span>{(asset.sizeBytes / 1024 / 1024).toFixed(1)} MB</span>}
        </div>)}
      </section>)}
    </div>
    <div className="flex flex-wrap gap-2">
      {!phaseMode && <Button size="sm" variant="outline" disabled={active} onClick={() => onRun("publish.snapshot", true)}>Inspect / dry run</Button>}
      {!phaseMode && <Button size="sm" variant="outline" disabled={active || !data.canPublish}
        onClick={() => onRun("publish.snapshot", false)}>Publish snapshot</Button>}
      {delivery && delivery.status !== "revoked" && <Button size="sm" variant="destructive" disabled={revoke.isPending} onClick={() => setConfirmRevoke(true)}>Revoke portal access</Button>}
      <Button size="sm" variant="ghost" onClick={() => void view.refetch()}>Refresh inspection</Button>
    </div>
    {confirmRevoke && <div role="alertdialog" aria-label="Confirm portal revocation" className="rounded-lg border p-3 text-sm">
      <p>Block this delivery and all its portal sessions? Public originals and downloaded files cannot be revoked. This snapshot cannot be republished.</p>
      <div className="mt-2 flex gap-2">
        <Button size="sm" variant="destructive" disabled={revoke.isPending} onClick={() => revoke.mutate()}>Confirm revoke</Button>
        <Button size="sm" variant="outline" onClick={() => setConfirmRevoke(false)}>Cancel</Button>
      </div>
    </div>}
    {revoke.isError && <p role="alert" className="text-sm">Revocation failed. Refresh and retry.</p>}
  </div>;
}
